import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {FONT_PROXIMA_11} from 'styles/FontConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import {InputChangeEvent} from 'util/Types';

import ModifiedBase from './ModifiedBase';
import ModifiedSugar from './ModifiedSugar';
import {
  DEFAULT_MODIFIED_BASE_COLOR,
  DEFAULT_MODIFIED_SUGAR_COLOR,
  NucleotideComponent,
  RECOMMENDED_CUSTOM_BASE_COLORS,
  RECOMMENDED_CUSTOM_SUGAR_COLORS,
} from './MonomerConstants';

const NUCLEOTIDE_COMPONENT_TO_SVG: {[nucleotideComponent: string]: (color: string) => JSX.Element} = {
  [NucleotideComponent.SUGAR]: (color: string): JSX.Element => (
    <ModifiedSugar cx={8} cy={8} radius={6} stroke={color} />
  ),
  [NucleotideComponent.BASE]: (color: string): JSX.Element => (
    <ModifiedBase cx={8} cy={8} radius={7} fill={color} />
  ),
};

interface MonomerColorMenuProps {
  selectedColor: string;
  nucleotideComponent: NucleotideComponent;
  onSelectColor: (color: string) => void;
  allUsedColors: Array<string>;
  onSetIsOpen: (isOpen: boolean) => void;
}

function MonomerColorMenu({
  selectedColor,
  nucleotideComponent,
  onSelectColor,
  allUsedColors,
  onSetIsOpen,
}: MonomerColorMenuProps): JSX.Element {
  const recommendedColors =
    nucleotideComponent === NucleotideComponent.BASE
      ? RECOMMENDED_CUSTOM_BASE_COLORS
      : RECOMMENDED_CUSTOM_SUGAR_COLORS;
  const [usedColors, unusedColors] = recommendedColors.reduce(
    ([used, unused], color): Array<Array<string>> =>
      allUsedColors.includes(color.toUpperCase()) ? [[...used, color], unused] : [used, [...unused, color]],
    [[], []]
  );
  const [useCustomColor, setUseCustomColor] = useState(
    !recommendedColors.includes(selectedColor.toUpperCase())
  );
  const defaultColor =
    nucleotideComponent === NucleotideComponent.BASE
      ? DEFAULT_MODIFIED_BASE_COLOR
      : DEFAULT_MODIFIED_SUGAR_COLOR;
  const handleChangeUseCustomColor = (e: InputChangeEvent): void => {
    if (e.target.checked) {
      setUseCustomColor(true);
    } else {
      if (!recommendedColors.includes(selectedColor)) {
        onSelectColor(defaultColor);
      }
      setUseCustomColor(false);
    }
  };
  const menuSections = [
    {header: 'Recommended colors', colors: unusedColors},
    {header: 'Recently used', colors: usedColors},
  ];
  return (
    <div className={`menu ${css(styles.menuContainer)}`}>
      {[
        ...menuSections.map(({header, colors}) =>
          colors.length > 0 ? (
            <div key={header} className={css(styles.colorSection)}>
              <p className={css(styles.colorHeader)}>{header}</p>
              <ul className="menu-body">
                {colors.map((color) => (
                  <li
                    key={color}
                    className={`${css(styles.colorOption)} ${
                      color.toUpperCase() === selectedColor.toUpperCase() && css(styles.activeOption)
                    }`}
                    style={{backgroundColor: color}}
                    onClick={() => {
                      onSelectColor(color);
                      onSetIsOpen(false);
                    }}
                  />
                ))}
              </ul>
            </div>
          ) : null
        ),

        <div key="Custom" className={css(styles.customColorSection)}>
          <CheckboxInput
            id="use-custom-color-checkbox"
            label="Use custom color"
            checked={useCustomColor}
            onChange={handleChangeUseCustomColor}
            labelStyle={LabelStyle.SUBLABEL}
            className={css(styles.customColorCheckbox)}
          />
          {useCustomColor && (
            <input
              type="Color"
              value={selectedColor}
              className={css(styles.colorInput)}
              onChange={(e: InputChangeEvent) => {
                onSelectColor(e.target.value);
              }}
            />
          )}
        </div>,
      ]}
    </div>
  );
}

interface MonomerColorDropdownProps extends Omit<MonomerColorMenuProps, 'onSetIsOpen'> {
  monomerId?: string;
  isDisabled?: boolean;
}

export default function MonomerColorDropdown({
  selectedColor,
  nucleotideComponent,
  monomerId,
  onSelectColor,
  isDisabled,
  allUsedColors,
}: MonomerColorDropdownProps): JSX.Element {
  const [isOpen, setIsOpen] = useState<boolean | undefined>(undefined);
  return (
    <Dropdown
      key={monomerId}
      menu={
        <MonomerColorMenu
          selectedColor={selectedColor}
          nucleotideComponent={nucleotideComponent}
          onSelectColor={onSelectColor}
          allUsedColors={allUsedColors}
          onSetIsOpen={setIsOpen}
        />
      }
      isOpen={isOpen}
      closeOnInsideClick={false}
      onToggle={setIsOpen}
      alwaysDropUp={true}
    >
      <Button
        hideDropdownCaret={false}
        isDropdown={true}
        size={ButtonSize.SMALL}
        buttonStyle={ButtonStyle.TERTIARY_WHITE}
        fullWidth={false}
        icon={
          <svg className={css(styles.visualSvg)} viewBox="0 0 16 16">
            {NUCLEOTIDE_COMPONENT_TO_SVG[nucleotideComponent](selectedColor)}
          </svg>
        }
        disabled={isDisabled}
        dataTestAttrs={{
          component: 'MonomerColorDropdown',
          element: 'dropdownButton',
        }}
      />
    </Dropdown>
  );
}

const styles = StyleSheet.create({
  visualSvg: {
    width: '14px',
    height: '14px',
  },
  menuContainer: {
    width: '200px',
    padding: '10px 12px',
  },
  colorSection: {
    ':not(:first-child)': {
      marginTop: '8px',
    },
  },
  colorHeader: {
    ...FONT_PROXIMA_11,
    color: GRAY_DARKER,
  },
  colorOption: {
    width: '15px',
    height: '15px',
    display: 'inline-block',
    marginRight: '3px',
    cursor: 'pointer',
  },
  activeOption: {
    boxShadow: 'inset 0px 0px 0px 1px black, inset 0px 0px 0px 2px white',
  },
  colorInput: {
    padding: 0,
    margin: 0,
    width: 17,
    height: 21,
    minHeight: 21,
    marginTop: 2,
    border: 'none',
    cursor: 'pointer',
  },
  customColorCheckbox: {
    marginRight: 5,
  },
  customColorSection: {
    ':not(:first-child)': {
      marginTop: '8px',
    },
    display: 'flex',
    alignItems: 'center',
  },
});
